
import Vue from "vue";
import SiteFooter from "@/components/organisms/Footer.vue";
import Loading from "@/components/atoms/Loading.vue";
import { showNotifyDialog } from "@/lib/dialog";
import axiosBase from "axios";
import { News, NewsCategory } from "@/lib/models/general";
import { differenceInDays } from "date-fns";

const Paginate = require("vuejs-paginate");

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    SiteFooter,
    Loading,
    Paginate,
  },
  data() {
    return {
      news: [] as News[],
      limit: 15,
      currentPage: 1,
      totalCount: 0,
      selectedCategory: NewsCategory.all as NewsCategory,
      isLoaded: false,
    };
  },
  computed: {
    getPageCount() {
      return Math.ceil(this.totalCount / this.limit);
    },
    categories() {
      return [
        { value: NewsCategory.all, label: "すべて" },
        { value: NewsCategory.newFeature, label: "新機能" },
        { value: NewsCategory.commercialWork, label: "商業作品" },
        { value: NewsCategory.contestCampaign, label: "コンテスト・キャンペーン" },
        { value: NewsCategory.companyBoard, label: "編集部の掲示板" },
        { value: NewsCategory.interview, label: "インタビュー" },
        { value: NewsCategory.bugMaintenance, label: "不具合・メンテナンス" },
        { value: NewsCategory.other, label: "その他" },
      ];
    },
  },
  methods: {
    async getNews() {
      try {
        // localeフィールドが["ja-JP"]と未設定のもののみを取得
        let filters = "locale[not_exists][or]locale[contains]ja-JP";

        // publishedAtが存在するもののみを取得
        filters += "[and]publishedAt[exists]";

        // カテゴリが選択されている場合（all以外）、カテゴリフィルタを追加
        // NOTE: categoryIdは配列として返ってくるため、[contains]オペレーターを使用
        if (this.selectedCategory !== NewsCategory.all) {
          filters += `[and]categoryId[contains]${this.selectedCategory}`;
        }

        const url = `/news?limit=${this.limit}&offset=${
          (this.currentPage - 1) * this.limit
        }&filters=${filters}&orders=-publishedAt`;
        const newsRequest = await axios.get(url);

        if (newsRequest.status !== 200) {
          await showNotifyDialog({
            title: "エラー",
            content: "お知らせの取得に失敗しました。",
          });
        }
        this.news = newsRequest.data.contents;
        this.totalCount = newsRequest.data.totalCount;
      } catch (error) {
        await showNotifyDialog({
          title: "エラー",
          content: "お知らせの取得に失敗しました。",
        });
      } finally {
        this.isLoaded = true;
      }
    },
    readNews() {
      if (this.news.length === 0) return;
      const latestNews = this.news[0];
      this.$store.dispatch("generalModule/readNews", latestNews);
    },
    clickCallback(pageNum) {
      this.currentPage = Number(pageNum);
      this.getNews();
      window.scrollTo(0, 0);
    },
    onCategoryChange(category: NewsCategory) {
      this.selectedCategory = category;
      this.currentPage = 1; // ページをリセット
      this.getNews();
      window.scrollTo(0, 0);
    },
    formatDate(dateString: string) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}年${month}月${day}日`;
    },
    getCategoryLabel(newsItem: News) {
      // 1. categoryCustomTextがあれば最優先で返す
      if (newsItem.categoryCustomText) {
        return newsItem.categoryCustomText;
      }

      // 2. categoryIdがない、または空配列の場合は「該当カテゴリなし」を返す
      if (!newsItem.categoryId || newsItem.categoryId.length === 0) {
        return "該当カテゴリなし";
      }

      // 3. 最初のcategoryIdに対応するカテゴリを検索
      const categoryValue = newsItem.categoryId[0];
      const category = this.categories.find((cat) => cat.value === categoryValue);

      // 4. 該当するカテゴリが見つからない場合は「該当カテゴリなし」を返す
      if (!category) {
        return "該当カテゴリなし";
      }

      // 5. 該当するカテゴリのラベルを返す
      return category.label;
    },
    isNewItem(publishedAt: string) {
      if (!publishedAt) return false;
      const publishedDate = new Date(publishedAt);
      const now = new Date();
      const daysDiff = differenceInDays(now, publishedDate);
      return daysDiff < 7;
    },
  },
  async created() {
    await this.getNews();
    this.readNews();
  },
});

interface Props {}

interface Data {
  news: News[];
  limit: number;
  currentPage: number;
  totalCount: number;
  selectedCategory: NewsCategory;
  isLoaded: boolean;
}

interface Computed {
  getPageCount: number;
  categories: Array<{ value: NewsCategory; label: string }>;
}

interface Methods {
  getNews(): Promise<void>;
  readNews(): void;
  clickCallback(pageNum: any): void;
  onCategoryChange(category: NewsCategory): void;
  formatDate(dateString: string): string;
  getCategoryLabel(newsItem: News): string;
  isNewItem(publishedAt: string): boolean;
}
